import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  medicalClinicNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
  type LearnSection,
  type LearnSource,
  type TOCItem,
  type CTABand,
} from "./learnShared";
import type { RelatedLink } from "@/components/RelatedPages";

/**
 * Factory for building LearnPageData from plain-text content.
 * Eliminates the repetitive JSX boilerplate across 40+ articles.
 *
 * Sections are provided as { heading, paragraphs[] } — each paragraph
 * becomes a <p>. Lists are { heading, list: string[] }. Tables are
 * { heading, headers[], rows[][] }.
 */

export interface PlainSection {
  heading: string;
  paragraphs?: string[];
  paragraphsAfter?: string[];
  list?: string[];
  table?: { headers: string[]; rows: string[][] };
  links?: { to: string; text: string; suffix?: string }[];
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function renderSection(s: PlainSection): LearnSection {
  const body: ReactNode[] = [];

  if (s.paragraphs) {
    s.paragraphs.forEach((p) => {
      // Support inline links via [[link text|/path]] syntax
      const parts = p.split(/(\[\[.+?\]\])/g);
      body.push(
        <p key={body.length}>
          {parts.map((part, i) => {
            const match = part.match(/^\[\[(.+)\|(.+)\]\]$/);
            if (match) {
              return (
                <a
                  key={i}
                  href={match[2]}
                  className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
                >
                  {match[1]}
                </a>
              );
            }
            return <span key={i}>{part}</span>;
          })}
        </p>,
      );
    });
  }

  if (s.list) {
    body.push(
      <ul key={body.length} className="mt-4 space-y-2">
        {s.list.map((item, i) => (
          <li
            key={i}
            className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent"
          >
            {item}
          </li>
        ))}
      </ul>,
    );
  }

  if (s.table) {
    body.push(
      <div key={body.length} className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {s.table.headers.map((h, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left font-semibold text-primary"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.table.rows.map((row, i) => (
              <tr key={i} className="border-b border-border/50">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-foreground/85">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
  }

  if (s.paragraphsAfter) {
    s.paragraphsAfter.forEach((p) => {
      const parts = p.split(/(\[\[.+?\]\])/g);
      body.push(
        <p key={body.length} className="mt-4">
          {parts.map((part, i) => {
            const match = part.match(/^\[\[(.+)\|(.+)\]\]$/);
            if (match) {
              return (
                <a
                  key={i}
                  href={match[2]}
                  className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
                >
                  {match[1]}
                </a>
              );
            }
            return <span key={i}>{part}</span>;
          })}
        </p>,
      );
    });
  }

  if (s.links) {
    body.push(
      <p key={body.length} className="mt-3">
        {s.links.map((l, i) => (
          <span key={i}>
            {i > 0 && " · "}
            <a
              href={l.to}
              className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
            >
              {l.text}
            </a>
            {l.suffix || ""}
          </span>
        ))}
      </p>,
    );
  }

  return { heading: s.heading, body: <>{body}</> };
}

export interface ArticleInput {
  slug: string;
  title: string;
  description: string;
  canonical: string;
  h1: string;
  breadcrumbLabel: string;
  crumbs?: { name: string; href: string }[];
  shortAnswer: string;
  keyTakeaways: string[];
  sections: PlainSection[];
  faqs: LearnFAQ[];
  sources: LearnSource[];
  about?: object | object[];
  relatedLinks: RelatedLink[];
  reviewBlock?: LearnPageData["reviewBlock"];
  toc?: TOCItem[];
  safetyBlock?: string;
  trademarkFooter?: string;
  ctaBand?: CTABand;
}

export function makeArticle(input: ArticleInput): LearnPageData {
  const reviewBlock = input.reviewBlock || {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  };

  return {
    slug: input.slug,
    title: input.title,
    description: input.description,
    canonical: input.canonical,
    h1: input.h1,
    breadcrumbLabel: input.breadcrumbLabel,
    crumbs: input.crumbs || baseCrumbs(input.breadcrumbLabel),
    shortAnswer: input.shortAnswer,
    keyTakeaways: input.keyTakeaways,
    sections: input.sections.map(renderSection),
    faqs: input.faqs,
    sources: input.sources,
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        webPageNode({
          id: `${SITE_URL}${input.canonical}#webpage`,
          name: input.h1,
          url: `${SITE_URL}${input.canonical}`,
          description: input.description,
          about: input.about,
          reviewedBy: reviewBlock.reviewedBy
            ? {
                name: reviewBlock.reviewedBy,
                url: reviewBlock.reviewedByHref
                  ? `${SITE_URL}${reviewBlock.reviewedByHref}`
                  : undefined,
              }
            : undefined,
          lastReviewed: reviewBlock.lastReviewed,
        }),
        medicalClinicNode,
        orgNode,
        faqJsonld(input.faqs, `${SITE_URL}${input.canonical}#faq`),
        breadcrumbJsonld(input.crumbs || baseCrumbs(input.breadcrumbLabel)),
      ],
    },
    relatedLinks: input.relatedLinks,
    reviewBlock: {
      ...reviewBlock,
      reviewedByHref: reviewBlock.reviewedByHref,
    },
    toc: input.toc,
    safetyBlock: input.safetyBlock,
    trademarkFooter: input.trademarkFooter,
    ctaBand: input.ctaBand,
  };
}

/* Shared related-link sets */

export const verifiedProviderRelated: RelatedLink[] = [
  {
    title: "Find a Verified SAINT® Provider",
    href: "/find-a-verified-saint-provider",
    description: "Verify a SAINT® provider and see our Magnus Medical listing",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is SAINT® TMS?",
    href: "/learn/what-is-saint-tms",
    description: "FDA clearance, protocol, and how SAINT differs from TMS",
  },
  {
    title: "SAINT® TMS Cost",
    href: "/learn/saint-tms-cost",
    description: "What SAINT® costs and how to plan for payment",
  },
  {
    title: "Does Insurance Cover SAINT® TMS?",
    href: "/learn/does-insurance-cover-saint-tms",
    description: "Coverage, prior authorization, and benefits verification",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
];

export const trdRelated: RelatedLink[] = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description:
      "The 5-day, fMRI-guided protocol for treatment-resistant depression",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition, causes, and treatment options",
  },
  {
    title: "When Antidepressants Don't Work",
    href: "/learn/when-antidepressants-dont-work",
    description: "What to do when medication isn't enough",
  },
  {
    title: "TMS Therapy",
    href: "/services/tms",
    description: "FDA-cleared TMS for depression",
  },
  {
    title: "SPRAVATO® (Esketamine)",
    href: "/services/spravato",
    description: "FDA-approved nasal spray for treatment-resistant depression",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
];

export const saintQuestionRelated: RelatedLink[] = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is SAINT® TMS?",
    href: "/learn/what-is-saint-tms",
    description: "FDA clearance, protocol, and evidence",
  },
  {
    title: "Find a Verified SAINT® Provider",
    href: "/find-a-verified-saint-provider",
    description: "How to verify a SAINT® provider",
  },
  {
    title: "What Happens During SAINT®",
    href: "/learn/what-to-expect-during-saint-tms",
    description: "Day-by-day guide to the five-day protocol",
  },
  {
    title: "SAINT® vs Traditional TMS",
    href: "/learn/saint-vs-traditional-tms",
    description: "How SAINT compares to standard TMS",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
];

export const saintRelated: RelatedLink[] = saintQuestionRelated;

export const notWorkingRelated: RelatedLink[] = [
  {
    title: "When Treatment Isn't Working",
    href: "/depression-treatment-not-working",
    description: "What to do when depression treatment isn't working",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description:
      "The 5-day, fMRI-guided protocol for treatment-resistant depression",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition, causes, and treatment options",
  },
  {
    title: "When Antidepressants Don't Work",
    href: "/learn/when-antidepressants-dont-work",
    description: "What to do when medication isn't enough",
  },
  {
    title: "Depression Treatment Beyond Medication",
    href: "/learn/depression-treatment-without-more-medication",
    description: "Non-medication options for depression",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
];
